import React from 'react';
import PropTypes from 'prop-types';

import Grid from '@mui/material/Grid';
import {styled} from '@mui/system';

import {concatUrl} from '../helpers/url';
import {getCall, postCall} from '../helpers/request';
import GlobalContext from '../helpers/context';
import history from '../helpers/history';
import {ROOT_PATH} from '../settings';
import ResearcherId from '../component/molecule/ResearcherId';
import ActionButton from '../component/atoms/ActionButton';
import Organization from '../component/molecule/Organization';
import ProfileDetails from '../component/organism/ProfileDetails';
import ProgressSpinner from '../component/atoms/ProgressSpinner';
import DocumentStats from '../component/molecule/DocumentStats';
import CustomAccordion from '../component/molecule/CustomAccordion';
import Box from "@mui/material/Box";


const StyledIgnoredProfiles = styled('div')({
    marginTop: 20,
    marginBottom: 30
});

const StyledIgnoredProfilesHeader = styled(Grid)(({ theme }) =>({
    padding: '10px 41px 10px 16px',
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    fontWeight: theme.custom.font.bold
}));

const StyledIgnoredListItem = styled(Grid)(({ theme }) =>({
    fontSize: theme.custom.font.medium
}));

const StyledAccordionDetails = styled('div')(({ theme }) =>({
    fontSize: theme.custom.font.medium
}));

const StyledProfileOptions = styled('div')({
    textAlign: 'right',
    paddingRight: 15
});

const StyledNoData = styled('div')(({ theme }) =>({
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize,
    marginTop: 30,
    marginBottom: 30,
    textAlign: 'center'
}));


class CurationProfileIgnored extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            ignored_profiles: undefined,
            profile_id: undefined,
            profile_data: {}
        };

        this.is_mounted = false;
        this.get_ignored_profiles_url = concatUrl(window.ROOT_URL, 'api', 'curation_profile', 'ignored', 'get');
        this.delete_ignored_profiles_url = concatUrl(window.ROOT_URL, 'api', 'curation_profile', 'ignored', 'delete');
        this.load_profile_data_url = concatUrl(window.ROOT_URL, 'api', 'profile', 'data');
    }

    componentDidMount(){
        this.is_mounted = true;
        if (this.props.curation_profile_id){
            this.loadIgnoredProfiles();
        }
    }

    componentWillUnmount(){
        this.is_mounted = false;
    }

    stopPropagation = (event) => {
        event.stopPropagation();
    };

    // +++ load ignored profiles +++++++++++++++++++++++++++++++++++++++++++++++

    loadIgnoredProfilesFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.context.displayGlobalError('Backend service failure: Could not load ignored profiles.');
    };

    loadIgnoredProfilesDone = (data, callback_failure) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        if (!data) {
            callback_failure();
        }else{
            this.setState({
                ignored_profiles: data.ignored_profiles
            });
        }
    };

    loadIgnoredProfiles = () => {
        getCall(`${this.get_ignored_profiles_url}?curation_profile_id=${this.props.curation_profile_id}&workspace_id=${this.props.workspace_id}`,
            this.loadIgnoredProfilesDone,
            this.loadIgnoredProfilesFailed);
    };


    // +++ delete ignored profile +++++++++++++++++++++++++++++++++++++++++++++++

    deleteIgnoredProfileFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.context.displayGlobalError('Backend service failure: Could not delete the profile from ignored list.');
    };

    deleteIgnoredProfileDone = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.setState({
            profile_id: undefined
        });
        this.loadIgnoredProfiles();
    };

    deleteIgnoredProfile = () => {
        let data = {
            curation_profile_id: this.props.curation_profile_id,
            profile_id: this.state.profile_id,
            workspace_id: this.props.workspace_id
        };
        postCall(this.delete_ignored_profiles_url, data, this.deleteIgnoredProfileDone, this.deleteIgnoredProfileFailed);
    };

    resetProfileId = () => {
        this.setState({
            profile_id: undefined
        });
    };

    showDeleteIgnoredProfileConfirmationDialog = (event) => {
        this.stopPropagation(event);

        this.setState({
            profile_id: event.currentTarget.dataset.profile_id,
        });

        this.context.showConfirmationDialog({
            confirm_dialog_on_close: this.resetProfileId,
            confirm_dialog_on_confirm: this.deleteIgnoredProfile,
            confirm_dialog_title: 'Please confirm',
            confirm_dialog_text: `Are you sure you want to delete the profile "${event.currentTarget.dataset.name}" (${event.currentTarget.dataset.researcher_id}) from the list of ignored profiles?`,
            confirm_dialog_button_text: 'delete'
        });
    };


    // +++ load profile data +++++++++++++++++++++++++++++++++++++++++++++++

    loadProfileDataFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.context.displayGlobalError('Backend service failure: Could not load profile data.');
    };

    loadProfileDataDone = (data) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        if (data && data.no_access){
            history.push(ROOT_PATH);
        } else {
            let profile_data = this.state.profile_data;
            profile_data[data.profile_id] = data.profile_data;
            this.setState({
                profile_data: profile_data
            });
        }
    };

    loadProfileData = (profile_id) => {
        getCall(this.load_profile_data_url + '?profile_id=' + profile_id + '&curation_profile_id=' + this.props.curation_profile_id, this.loadProfileDataDone, this.loadProfileDataFailed);
    };

    handleExpansionPanelChange = profile_id => () => {
        if(!this.state.profile_data[profile_id]){
            this.loadProfileData(profile_id);
        }
    };

    topOneOrganization = (profile_data) => {
        if(profile_data && profile_data.current_organization){
            return <Organization organization={profile_data.current_organization} profileListStyle={true}/>;
        }
    };

    ignoredProfiles(){
        if(this.state.ignored_profiles === undefined) {
            return (
                <Grid container>
                    <Grid size={12}>
                        <ProgressSpinner/>
                    </Grid>
                </Grid>
            );
        }
        if(this.state.ignored_profiles.length > 0){
            return (
                <div>
                    {this.state.ignored_profiles.map((profile, index) => <CustomAccordion
                        key={`ign_${index}`}
                        onOpenCallback={this.handleExpansionPanelChange(profile.profile_id)}
                        summary={
                            <Box sx={{ flexGrow: 1 }}>
                                <StyledIgnoredListItem container spacing={2}>
                                    <Grid size={3}>
                                        {profile.full_name}
                                    </Grid>
                                    <Grid size={4}>
                                        {this.topOneOrganization(profile)}
                                    </Grid>
                                    <Grid size={2}>
                                        <DocumentStats profile={profile} />
                                    </Grid>
                                    <Grid size={2}>
                                        <ResearcherId id={profile.researcher_id} />
                                    </Grid>
                                    <StyledProfileOptions size={1}>
                                        <ActionButton text="Delete" linkStyle={true}
                                                      actionCallback={this.showDeleteIgnoredProfileConfirmationDialog}
                                                      actionData={
                                                          {
                                                              profile_id: profile.profile_id,
                                                              researcher_id: profile.researcher_id,
                                                              name: profile.full_name
                                                          }
                                                      } />
                                    </StyledProfileOptions>
                                </StyledIgnoredListItem>
                            </Box>
                        }
                        content={
                        <StyledAccordionDetails>
                            <ProfileDetails profile={this.state.profile_data[profile.profile_id]} />
                        </StyledAccordionDetails>
                        }
                    />)}
                </div>
            );
        }
        return (
            <StyledNoData>
                No ignored profiles available
            </StyledNoData>
        );
    }

    render() {
        return (
            <GlobalContext.Consumer>{() => (
                <StyledIgnoredProfiles>
                    <StyledIgnoredProfilesHeader container spacing={2}>
                        <Grid size={3}>
                            Name
                        </Grid>
                        <Grid size={4}>
                            Organization <br/>
                            City, State, Country
                        </Grid>
                        <Grid size={2}>
                            Publications <br/>
                            Grants
                        </Grid>
                        <Grid size={2}>
                            Researcher ID
                        </Grid>
                        <Grid size={1} />
                    </StyledIgnoredProfilesHeader>
                    {this.ignoredProfiles()}
                </StyledIgnoredProfiles>
            )}
            </GlobalContext.Consumer>
        );
    }
}


CurationProfileIgnored.contextType = GlobalContext;

CurationProfileIgnored.propTypes = {
    curation_profile_id: PropTypes.number.isRequired,
    workspace_id: PropTypes.number.isRequired
};

export default CurationProfileIgnored;
